import cx from 'classnames';
import { DateTime } from 'luxon';
import { groupBy } from 'lodash';
import InfiniteScroll from 'react-infinite-scroller';
import { useLocation, useHistory } from 'react-router';
import React, { useContext, useMemo, useState, ReactNode } from 'react';
import { animated, useTransition } from 'react-spring';

import { Greet, GreetSendType } from '~/types/models';
import TodoItem from '../Components/TodoItem';
import { parseUtcDate, utcToLocalDate } from '@vimily/bonjoro-ui-components-v2';
import ListFallback from '~/App/Modules/Common/Components/List/Loading';
import TodoListContext from '~/App/Modules/TodoList/Context';
import MobileAppModal, {
  MobileAppModalType,
} from '~/App/Components/Modals/MobileAppModal';
import { isMobileDevice as isMobileChecker } from '~/utils/agent';
import { ScreenSize, useScreenSize } from '~/react-hooks/use-screen-size';
import Context from '../Context';
import {
  TASK_LIST_BULK_SELECT_HEIGHT,
  TASK_LIST_HEADER_HEIGHT,
} from '../Containers/HeaderContainer';
import { NAV_HEADER_HEIGHT_IN_PX } from '~/App/Components/NavHeaderItems';
import { assetUrl } from '~/helpers';
interface NoTodosProps {
  className?: string;
}

const NoTodos = ({ className }: NoTodosProps) => {
  return (
    <div className={cx('pt-[56px] text-center', className)}>
      <div className="flex flex-col gap-y-[15px] px-[30px] v2-text-neutral-500 items-center">
        <img
          className='w-[200px] h-[160px] object-cover'
          src={assetUrl('assets/images/joro/ready-set-action.png')}
        />
        <span className="text-[21px] font-bold">Your tasklist is empty!</span>
        <span className="text-base">Add new contacts to send them personalised messages</span>
      </div>
    </div>
  );
};

interface DayItemProps {
  text: React.ReactNode;
}

const DayItem = ({ text }: DayItemProps) => {
  return <div className='task-separator v2-bg-neutral-100'>{text}</div>;
};

type GreetListItemProps = {
  greet: Greet;
  onClick: (url: string) => void;
};

function GreetListItem({ greet, onClick }: GreetListItemProps) {
  const location = useLocation();
  const matchUrl = `/home/greet/${greet.id}`;

  return (
    <TodoItem
      id={greet.id}
      className='task-simple-list-item cursor-pointer'
      selected={location.pathname.startsWith(matchUrl)}
      profiles={greet.profiles}
      reason={greet.event?.message}
      timestamp={greet.updated_at}
      icons={
        greet?.send_type === GreetSendType.Fallback
          ? <i className='!w-3 !h-3 icon icon-timer !v2-bg-turquoise' />
          : undefined
      }
      onClick={() => onClick(matchUrl)}
    />
  );
}

function List() {
  const history = useHistory();
  const { bulkSelect } = useContext(Context);

  const { greets, showFallback, container } = useContext(TodoListContext);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const showEmptyState = useMemo(
    () => greets.data.length === 0 && !greets.metadata.loading,
    [greets.metadata.loading, greets.data]
  );

  const screenSize = useScreenSize();
  const isSmallDevice = useMemo(
    () => isMobileChecker() && screenSize < ScreenSize.Medium,
    [screenSize]
  );

  const greetsByDay = useMemo(() => {
    return groupBy<Greet>(greets.data, (greet: Greet) => {
      const utcDate = parseUtcDate(greet.updated_at);
      const localDate = utcToLocalDate(utcDate);
      const startOfDay = localDate.startOf('day');

      return startOfDay.toISODate();
    });
  }, [greets.data]);

  const days = useMemo<string[]>(() => Object.keys(greetsByDay), [greets.data]);

  const transitions = useTransition(showFallback, null, {
    from: { opacity: 0, position: 'absolute', width: '100%' },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const filterHeight = !bulkSelect.data.any
    ? TASK_LIST_HEADER_HEIGHT
    : TASK_LIST_BULK_SELECT_HEIGHT;
  const listHeight =
    container.height - (NAV_HEADER_HEIGHT_IN_PX + filterHeight);

  const onGreetClicked = (matchUrl: string) => {
    if (isSmallDevice) {
      setIsMobileModalOpen(true);
      return;
    }
    history.push(matchUrl);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {transitions.map(({ key, item: showFallback, props }) => {
        props = { ...props };

        if (showFallback) {
          return (
            <animated.div key={key} style={props}>
              <ListFallback />
            </animated.div>
          );
        }

        return (
          <animated.div key={key} style={props}>
            {listHeight > 0 && (
              <>
                <MobileAppModal
                  isOpen={isMobileModalOpen}
                  onModalClose={() => setIsMobileModalOpen(false)}
                  type={MobileAppModalType.DownloadApp}
                />
                <div
                  className={cx(
                    { hidden: showEmptyState },
                    'w-full h-full overflow-auto sm:overflow-initial'
                  )}
                  style={{ height: listHeight }}
                >
                  <InfiniteScroll
                    loadMore={greets.paginator.more}
                    hasMore={greets.metadata.canFetchMore}
                    useWindow={false}
                    pageStart={1}
                  >
                    {days.reduce<ReactNode[]>((elements, day) => {
                      return [
                        ...elements,
                        <DayItem
                          key={day}
                          text={DateTime.fromISO(day).toFormat('MMMM d')}
                        />,
                        ...greetsByDay[day].map((greet) => (
                          <GreetListItem
                            key={greet.id}
                            greet={greet}
                            onClick={onGreetClicked}
                          />
                        )),
                      ];
                    }, [])}
                  </InfiniteScroll>
                </div>
              </>
            )}
            <NoTodos className={cx({ hidden: !showEmptyState })} />
          </animated.div>
        );
      })}
    </div>
  );
}

export default List;
